/**
 * One client's every mapping, opened under its row in the master table
 * (roadmap MR-2; ADR 0078, and ADR 0082: it was a side panel until the
 * owner tested rc.14): Axon's Autotask settings, the ScreenConnect company,
 * the Datto RMM site (ADR 0080), the phone numbers, and the email domains
 * (ADR 0143). The billing type is set in the row itself.
 *
 * Each change saves at once, and the API checks it again. Values stay in their
 * fields: a ScreenConnect company shows as a chip, and × clears it.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useId, useState } from "react";

import { ApiError } from "../../api/client";
import type {
  AddedEmailDomain,
  AddedNetworkAddress,
  AutotaskNetworkEntry,
  ClientChanges,
  ClientEmailDomains,
  ClientMapping,
  ClientRecord,
  DattoSites,
  ScreenConnectCompanies,
} from "../../api/types";
import { useSession } from "../../app/session-context";
import { useOneSaveAtATime } from "../../app/useOneSaveAtATime";
import { Combobox, type ComboboxOption } from "../../components/ui/Combobox";
import { formatDateTimeIn } from "../../lib/dates";
import { covers } from "../../lib/networkRanges";
import { formatPhone } from "../../lib/phones";
import { possessive } from "../../lib/possessive";
import { CLIENTS_KEY, DATTO_SITES_KEY, SCREENCONNECT_KEY } from "./keys";
import { sectionTitle } from "./summaryRows";

const inputClass =
  "h-10 w-full rounded-input border border-line bg-card px-3 text-text " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand";

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.fieldErrors[0]?.message ?? error.message;
  }
  return error instanceof Error ? error.message : "The change was not saved.";
}

function Refusal({ error }: { error: string | null }) {
  return error === null ? null : (
    <p role="alert" className="text-sm text-status-needs-client-fg">
      Not saved: {error}
    </p>
  );
}

function AutotaskSection({
  client,
  onSaved,
}: {
  client: ClientMapping;
  onSaved: (record: ClientRecord) => void;
}) {
  const { api } = useSession();
  const contractId = useId();
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (changes: ClientChanges) => api.updateClient(client.id, changes),
    onSuccess: (record) => {
      setError(null);
      onSaved(record);
    },
    onError: (e) => {
      setError(errorMessage(e));
    },
  });
  // One save at a time, with the field still usable (#241).
  const { send, saving } = useOneSaveAtATime(save, (queued, value) => ({ ...queued, ...value }));
  const contract = client.contracts.find(
    (c) => c.autotask_contract_id === client.default_contract_id,
  );
  // The API lists the contracts that can be the default today, and the saved
  // one if it can't any more, marked (ADR 0101): shown, to be changed here.
  const savedProblem =
    client.default_contract_id === null
      ? null
      : contract === undefined
        ? "not in Autotask"
        : contract.available
          ? null
          : (contract.unavailable_reason ?? "not in Autotask");
  const problemId = `${contractId}-problem`;

  return (
    <section aria-label="Autotask" className="flex flex-col gap-3">
      <h4 className={sectionTitle}>Autotask</h4>
      <div className="flex flex-wrap gap-3">
        <div className="flex min-w-48 grow flex-col gap-1">
          <label htmlFor={contractId} className="text-xs font-semibold text-muted">
            Default contract
          </label>
          {client.contracts.length === 0 && client.default_contract_id === null ? (
            <span className="flex h-10 items-center text-sm text-muted">No active contracts</span>
          ) : (
            <select
              id={contractId}
              className={inputClass}
              aria-invalid={savedProblem !== null}
              aria-describedby={savedProblem !== null ? problemId : undefined}
              value={String(
                (saving && saving.value.default_contract_id !== undefined
                  ? saving.value.default_contract_id
                  : client.default_contract_id) ?? "",
              )}
              onChange={(event) => {
                const value = event.target.value;
                send({ default_contract_id: value === "" ? null : Number(value) });
              }}
            >
              <option value="">None</option>
              {client.default_contract_id !== null && contract === undefined ? (
                <option value={String(client.default_contract_id)}>
                  {`${String(client.default_contract_id)} (not in Autotask)`}
                </option>
              ) : null}
              {client.contracts
                .filter((c) => c.available || c.autotask_contract_id === client.default_contract_id)
                .map((c) => (
                  <option key={c.autotask_contract_id} value={String(c.autotask_contract_id)}>
                    {`${c.name} · ${String(c.autotask_contract_id)}` +
                      (c.available ? "" : ` (${c.unavailable_reason ?? "not in Autotask"})`)}
                  </option>
                ))}
            </select>
          )}
          {savedProblem !== null ? (
            <p
              id={problemId}
              className="rounded-row bg-status-needs-client-bg px-3 py-1 text-sm text-status-needs-client-fg"
            >
              {`Needs attention: this contract can't be used (${savedProblem}). Choose another.`}
            </p>
          ) : null}
        </div>
      </div>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={client.is_hipaa}
          onChange={(event) => {
            const turningOff = !event.target.checked;
            // Turning HIPAA off lets the app store details it withholds today
            // (section 14.4), so it asks first.
            if (
              turningOff &&
              !window.confirm(
                `Turn off HIPAA handling for ${client.name}? Axon Time will then store ` +
                  "subject lines and other details it currently withholds for this client.",
              )
            ) {
              return;
            }
            send({ is_hipaa: event.target.checked });
          }}
          className="size-4 accent-brand"
        />
        HIPAA: keep only what section 14.4 allows about this client&apos;s activity
      </label>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={client.is_internal}
          onChange={(event) => {
            send({ is_internal: event.target.checked });
          }}
          className="size-4 accent-brand"
        />
        Internal: Axon&apos;s own time
      </label>
      <Refusal error={error} />
    </section>
  );
}

function ScreenConnectSection({
  client,
  clients,
  companies,
}: {
  client: ClientMapping;
  clients: ClientMapping[];
  companies: { data?: ScreenConnectCompanies | undefined; isError: boolean };
}) {
  const { api, me } = useSession();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (name: string | null) => api.setScreenConnectCompany(client.id, name),
    onSuccess: () => {
      setError(null);
      return queryClient.invalidateQueries({ queryKey: SCREENCONNECT_KEY });
    },
    onError: (e) => {
      setError(errorMessage(e));
    },
  });
  // One save at a time, with the field still usable (#241).
  const { send } = useOneSaveAtATime(save);
  const data = companies.data;
  const names = new Map(clients.map((c) => [c.id, c.name]));
  const mine = (data?.companies ?? []).filter((c) => c.client_id === client.id);
  const suggested = (data?.suggestions ?? [])
    .filter((s) => s.client_id === client.id)
    .sort((a, b) => b.score - a.score);
  const scores = new Map(suggested.map((s) => [s.value, s.score]));
  const byValue = new Map((data?.companies ?? []).map((c) => [c.value, c]));
  // Suggestions first, then the companies on no client, then those on another.
  const rank = (value: string, clientId: string | null) =>
    scores.has(value) ? 0 : clientId === null || clientId === client.id ? 1 : 2;
  const options: ComboboxOption[] = [...(data?.companies ?? [])]
    .sort((a, b) => rank(a.value, a.client_id) - rank(b.value, b.client_id))
    .map((c) => {
      const others = c.client_id !== null && c.client_id !== client.id;
      const score = scores.get(c.value);
      return {
        value: c.value,
        label: c.name,
        detail: others
          ? possessive(names.get(c.client_id ?? "") ?? "another client")
          : score !== undefined
            ? `${String(score)}% alike`
            : c.listed !== null
              ? `${String(c.listed)} in ScreenConnect`
              : undefined,
        disabled: others,
      };
    });
  const [current] = mine;
  const top = suggested[0];
  const topCompany = top ? byValue.get(top.value) : undefined;

  return (
    <section aria-label="ScreenConnect company" className="flex flex-col gap-2">
      <h4 className={sectionTitle}>ScreenConnect company</h4>
      <Combobox
        label="ScreenConnect company"
        hideLabel
        options={options}
        value={mine.length === 1 && current ? current.value : ""}
        loading={data === undefined && !companies.isError}
        failed={companies.isError}
        failedText="Couldn't load ScreenConnect's companies"
        placeholder={mine.length > 1 ? "Choose the one to keep" : "Not mapped"}
        onChange={(value) => {
          send(value === "" ? null : (byValue.get(value)?.name ?? value));
        }}
      />
      {mine.length > 1 ? (
        <p className="text-sm text-status-needs-client-fg">
          {`${String(mine.length)} companies from before (${mine.map((c) => c.name).join(", ")}). One per client now: choose the one to keep.`}
        </p>
      ) : null}
      {mine.length === 1 && current ? (
        <p className="text-sm text-muted">
          {[
            current.listed !== null ? `${String(current.listed)} sessions in ScreenConnect` : null,
            current.last_seen !== null
              ? `last connected ${formatDateTimeIn(current.last_seen, me.timezone)}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ") || "Not in ScreenConnect's company list"}
        </p>
      ) : null}
      {mine.length === 0 && top && topCompany ? (
        <button
          type="button"
          className="h-9 cursor-pointer self-start rounded-pill border border-line bg-card px-4 text-sm font-medium hover:bg-brand-tint disabled:opacity-50"
          onClick={() => {
            send(topCompany.name);
          }}
        >
          {`Use “${topCompany.name}” (${String(top.score)}% alike)`}
        </button>
      ) : null}
      <Refusal error={error} />
    </section>
  );
}

/**
 * The client's one Datto RMM site (ADR 0080). Datto's own Autotask link
 * decides it where there's one site for the company: shown, and changed in
 * Datto. Several for one company: the owner picks. None: the owner may choose
 * a site Datto links to nobody.
 */
function DattoSection({
  client,
  clients,
  sites,
}: {
  client: ClientMapping;
  clients: ClientMapping[];
  sites: { data?: DattoSites | undefined; isError: boolean };
}) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (uid: string | null) => api.setDattoSite(client.id, uid),
    onSuccess: () => {
      setError(null);
      return queryClient.invalidateQueries({ queryKey: DATTO_SITES_KEY });
    },
    onError: (e) => {
      setError(errorMessage(e));
    },
  });
  // One save at a time, with the field still usable (#241).
  const { send } = useOneSaveAtATime(save);
  const data = sites.data;
  const names = new Map(clients.map((c) => [c.id, c.name]));
  const listed = (data?.sites ?? []).filter((s) => !s.missing);
  const mine = listed.find((s) => s.client_id === client.id);
  const several = data?.several[client.id] ?? [];
  const byDatto = mine?.how === "datto";
  const options: ComboboxOption[] = listed.map((s) => {
    const others = s.client_id !== null && s.client_id !== client.id;
    return {
      value: s.uid,
      label: s.name || s.uid,
      detail: others
        ? possessive(names.get(s.client_id ?? "") ?? "another client")
        : several.includes(s.uid)
          ? "Datto links it here"
          : undefined,
      disabled: others,
    };
  });

  return (
    <section aria-label="Datto RMM site" className="flex flex-col gap-2">
      <h4 className={sectionTitle}>Datto RMM site</h4>
      <Combobox
        label="Datto RMM site"
        hideLabel
        options={options}
        value={mine?.uid ?? ""}
        loading={data === undefined && !sites.isError}
        failed={sites.isError}
        failedText="Couldn't load Datto's sites"
        disabled={byDatto}
        placeholder={several.length > 1 ? "Choose the client's site" : "Not mapped"}
        onChange={(uid) => {
          send(uid === "" ? null : uid);
        }}
      />
      {byDatto ? (
        <p className="text-sm text-muted">From Datto&apos;s Autotask link. Change it in Datto.</p>
      ) : null}
      {several.length > 1 && !mine ? (
        <p className="text-sm text-status-needs-client-fg">
          {`Datto links ${String(several.length)} sites to this company. One per client: choose the client's site. Until then, their activity matches nobody.`}
        </p>
      ) : null}
      <Refusal error={error} />
    </section>
  );
}

function PhonesSection({ client }: { client: ClientMapping }) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const key = ["client-phones", client.id];
  const phones = useQuery({ queryKey: key, queryFn: () => api.getClientPhones(client.id) });
  const [error, setError] = useState<string | null>(null);
  const forget = useMutation({
    mutationFn: (numberId: string) => api.forgetClientPhone(client.id, numberId),
    onSuccess: () => {
      setError(null);
      return queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (e) => {
      setError(errorMessage(e));
    },
  });
  const data = phones.data;

  return (
    <section aria-label="Phone numbers" className="flex flex-col gap-2">
      <h4 className={sectionTitle}>Phone numbers</h4>
      {phones.isError ? (
        <p role="alert" className="text-sm text-muted">
          The numbers could not be loaded.
        </p>
      ) : data === undefined ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : (
        <>
          <div className="flex flex-col gap-1 rounded-row bg-panel-2 px-3 py-2.5 text-sm">
            <span className="text-xs font-semibold text-muted">From Autotask</span>
            <span>
              {data.company_numbers.length > 0
                ? `Main line: ${data.company_numbers.map(formatPhone).join(", ")}`
                : "No main line in Autotask"}
            </span>
            <span>
              {`${String(data.contacts_with_numbers)} ${data.contacts_with_numbers === 1 ? "contact" : "contacts"} with numbers: a call from one matches the contact, and ${client.name}.`}
            </span>
            <span className="text-xs text-muted">Change these in Autotask. The sync brings them here.</span>
          </div>
          <span className="text-xs font-semibold text-muted">Only in Axon Time</span>
          {data.own.length === 0 ? (
            <p className="text-sm text-muted">None.</p>
          ) : (
            <ul aria-label="Numbers only in Axon Time" className="flex flex-col gap-1.5">
              {data.own.map((n) => (
                <li
                  key={n.id}
                  className="flex items-center gap-2 rounded-row border border-line px-3 py-1.5 text-sm"
                >
                  <span className="font-mono">{formatPhone(n.number)}</span>
                  <span className="text-xs text-muted">
                    {n.source === "learned"
                      ? `remembered${n.remembered_by ? ` by ${n.remembered_by}` : ""}`
                      : "added by an admin"}
                  </span>
                  <button
                    type="button"
                    aria-label={`Forget ${formatPhone(n.number)}`}
                    disabled={forget.isPending}
                    className="ml-auto cursor-pointer rounded-pill px-2 text-muted hover:bg-brand-tint disabled:opacity-50"
                    onClick={() => {
                      forget.mutate(n.id);
                    }}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Refusal error={error} />
        </>
      )}
    </section>
  );
}

/** What Autotask's Web Address gives to match by, in words (ADR 0143). */
function websiteLine(data: ClientEmailDomains): { said: ReactNode; hint: string } {
  const change = "Change these in Autotask. The sync brings them here.";
  if (data.web_address === null || data.web_address.trim() === "") {
    return { said: "No website in Autotask", hint: change };
  }
  if (data.website_domain === null) {
    return {
      said: `No main domain: the Web Address, “${data.web_address}”, isn't a website.`,
      hint: "Correct it in Autotask, or add the domain below.",
    };
  }
  if (data.website_domain_public) {
    return {
      said: `No main domain: ${data.website_domain} is a public email domain (Mappings → Email), so it never matches by domain.`,
      hint: change,
    };
  }
  return {
    said: (
      <>
        Main domain: <span className="font-mono">{data.website_domain}</span>
      </>
    ),
    hint: `From its Web Address, ${data.web_address}. ${change}`,
  };
}

/**
 * The client's email domains (ADR 0143): the one its Autotask Web Address
 * gives, read only, and any added here, several, each with who added it. The
 * API refuses one that can't be this client's, and says why.
 */
function EmailDomainsSection({ client }: { client: ClientMapping }) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const inputId = useId();
  const hintId = useId();
  const alertId = useId();
  const key = ["client-email-domains", client.id];
  const domains = useQuery({ queryKey: key, queryFn: () => api.getClientEmailDomains(client.id) });
  const [typed, setTyped] = useState("");
  const [refused, setRefused] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  // The table's Email column counts them too.
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: key }),
      queryClient.invalidateQueries({ queryKey: CLIENTS_KEY }),
    ]);
  const add = useMutation({
    mutationFn: (domain: string) => api.addClientEmailDomain(client.id, domain),
    onSuccess: (added) => {
      setTyped("");
      setRefused(null);
      setDone(`Added ${added.domain}.`);
      return refresh();
    },
    onError: (e) => {
      setDone(null);
      setRefused(`Not added: ${errorMessage(e)}`);
    },
  });
  const remove = useMutation({
    mutationFn: (domain: AddedEmailDomain) => api.removeClientEmailDomain(client.id, domain.id),
    onSuccess: (_, domain) => {
      setRefused(null);
      setDone(`Removed ${domain.domain}.`);
      return refresh();
    },
    onError: (e) => {
      setDone(null);
      setRefused(`Not removed: ${errorMessage(e)}`);
    },
  });
  const data = domains.data;
  const contacts = data?.contacts_with_addresses ?? 0;
  const website = data ? websiteLine(data) : null;

  return (
    <section aria-label="Email domains" className="flex flex-col gap-2">
      <h4 className={sectionTitle}>Email domains</h4>
      {domains.isError ? (
        <p role="alert" className="text-sm text-muted">
          The email domains could not be loaded.
        </p>
      ) : data === undefined || website === null ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : (
        <>
          <div className="flex flex-col gap-1 rounded-row bg-panel-2 px-3 py-2.5 text-sm">
            <span className="text-xs font-semibold text-muted">From Autotask</span>
            <span className="[overflow-wrap:anywhere]">{website.said}</span>
            <span>
              {`${String(contacts)} ${contacts === 1 ? "contact with an email address" : "contacts with email addresses"}: an email with one matches the contact, and ${client.name}.`}
            </span>
            <span className="text-xs text-muted [overflow-wrap:anywhere]">{website.hint}</span>
          </div>
          <span className="text-xs font-semibold text-muted">Added here</span>
          {data.added.length === 0 ? (
            <p className="text-sm text-muted">None.</p>
          ) : (
            <ul aria-label="Email domains added here" className="flex flex-col gap-1.5">
              {data.added.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center gap-2 rounded-row border border-line px-3 py-1.5 text-sm"
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="font-mono [overflow-wrap:anywhere]">{d.domain}</span>
                    {d.added_by ? (
                      <span className="text-xs text-muted">{`added by ${d.added_by}`}</span>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${d.domain}`}
                    disabled={remove.isPending}
                    className="ml-auto cursor-pointer rounded-pill px-2 text-muted hover:bg-brand-tint disabled:opacity-50"
                    onClick={() => {
                      remove.mutate(d);
                    }}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
          <form
            className="flex flex-wrap gap-2"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              if (typed.trim() !== "") {
                add.mutate(typed.trim());
              }
            }}
          >
            <label htmlFor={inputId} className="sr-only">
              {`Add an email domain for ${client.name}`}
            </label>
            <input
              id={inputId}
              type="text"
              placeholder="example.com"
              autoComplete="off"
              spellCheck={false}
              value={typed}
              aria-invalid={refused === null ? undefined : true}
              aria-describedby={refused === null ? hintId : `${hintId} ${alertId}`}
              className={`${inputClass} min-w-0 flex-[1_1_140px] font-mono text-sm`}
              onChange={(event) => {
                setTyped(event.target.value);
                setRefused(null);
                setDone(null);
              }}
            />
            <button
              type="submit"
              disabled={add.isPending || typed.trim() === ""}
              className="h-10 cursor-pointer rounded-pill border border-line bg-card px-4 text-sm font-medium hover:bg-brand-tint disabled:cursor-default disabled:opacity-50"
            >
              Add
            </button>
          </form>
          <span id={hintId} className="text-xs text-muted">
            {`Email with anyone at a domain added here matches ${client.name}; a subdomain is its own domain. Email already on a day matches once it's read again: Owner tools → Microsoft 365 → Missed email.`}
          </span>
          {refused !== null ? (
            <p id={alertId} role="alert" className="text-sm text-status-needs-client-fg">
              {refused}
            </p>
          ) : null}
          <p role="status" className="text-sm font-medium text-status-logged-fg empty:hidden">
            {done ?? ""}
          </p>
        </>
      )}
    </section>
  );
}

/**
 * One entry of the client's Site Configuration (ADR 0174): as typed in
 * Autotask, beside the range it covers ("203.0.113.39/29 · covers .32 to
 * .39"), and, if the sync found it entered wrong, what's wrong.
 */
function AutotaskEntryItem({ entry }: { entry: AutotaskNetworkEntry }) {
  const problemId = useId();
  const about = entry.address === null ? "not kept" : covers(entry.address, entry.typed);
  return (
    <li aria-describedby={entry.problem === null ? undefined : problemId} className="flex flex-col">
      <span className="[overflow-wrap:anywhere]">
        <span className="font-mono">{entry.typed ?? entry.address}</span>
        {about === null ? null : <span className="text-xs text-muted">{` · ${about}`}</span>}
      </span>
      {entry.problem === null ? null : (
        <span id={problemId} className="text-xs font-medium text-status-needs-client-fg">
          {`Wrong: ${entry.problem}`}
        </span>
      )}
    </li>
  );
}

/**
 * The client's network addresses (Phase 7; ADR 0158): its firewall's WAN
 * address, an iDRAC, a server, each an address or a range of at most a /24
 * (IPv4) or /64 (IPv6). Those from Autotask's Site Configuration tab are read
 * only (ADR 0171): they change in Autotask, as its phone numbers do. Each
 * shows as typed there, beside the range it covers, and one entered wrong
 * says what's wrong (ADR 0174). One added here that Autotask has too is
 * marked, to remove if it isn't needed. An address is one client's: the API
 * refuses one another client has, saying whose.
 */
function NetworkAddressesSection({ client }: { client: ClientMapping }) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const inputId = useId();
  const hintId = useId();
  const alertId = useId();
  const key = ["client-network-addresses", client.id];
  const addresses = useQuery({
    queryKey: key,
    queryFn: () => api.getClientNetworkAddresses(client.id),
  });
  const [typed, setTyped] = useState("");
  const [refused, setRefused] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });
  const add = useMutation({
    mutationFn: (address: string) => api.addClientNetworkAddress(client.id, address),
    onSuccess: (added) => {
      setTyped("");
      setRefused(null);
      setDone(`Added ${added.address}.`);
      return refresh();
    },
    onError: (e) => {
      setDone(null);
      setRefused(`Not added: ${errorMessage(e)}`);
    },
  });
  const remove = useMutation({
    mutationFn: (address: AddedNetworkAddress) =>
      api.removeClientNetworkAddress(client.id, address.id),
    onSuccess: (_, address) => {
      setRefused(null);
      setDone(`Removed ${address.address}.`);
      return refresh();
    },
    onError: (e) => {
      setDone(null);
      setRefused(`Not removed: ${errorMessage(e)}`);
    },
  });
  const data = addresses.data;

  return (
    <section aria-label="Network addresses" className="flex flex-col gap-2">
      <h4 className={sectionTitle}>Network addresses</h4>
      {addresses.isError ? (
        <p role="alert" className="text-sm text-muted">
          The network addresses could not be loaded.
        </p>
      ) : data === undefined ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : (
        <>
          <div className="flex flex-col gap-1 rounded-row bg-panel-2 px-3 py-2.5 text-sm">
            <span className="text-xs font-semibold text-muted">From Autotask</span>
            {data.autotask_entries.length === 0 ? (
              <span>No WAN address in Autotask</span>
            ) : (
              <ul aria-label={`Network addresses of ${client.name} from Autotask`} className="flex flex-col gap-1">
                {data.autotask_entries.map((entry, index) => (
                  <AutotaskEntryItem
                    key={`${entry.address ?? ""} ${entry.typed ?? ""} ${String(index)}`}
                    entry={entry}
                  />
                ))}
              </ul>
            )}
            <span className="text-xs text-muted">
              From its Site Configuration tab, WAN - Static IP. Change these in Autotask. The sync brings them here.
            </span>
          </div>
          <span className="text-xs font-semibold text-muted">Added here</span>
          {data.added.length === 0 ? (
            <p className="text-sm text-muted">None.</p>
          ) : (
            <ul aria-label={`Network addresses of ${client.name}`} className="flex flex-col gap-1.5">
              {data.added.map((a) => {
                const about = [
                  a.added_by ? `added by ${a.added_by}` : null,
                  a.also_from_autotask ? "also from Autotask" : null,
                ].filter((part) => part !== null);
                const range = covers(a.address, a.address);
                return (
                  <li
                    key={a.id}
                    className="flex items-center gap-2 rounded-row border border-line px-3 py-1.5 text-sm"
                  >
                    <span className="flex min-w-0 flex-col">
                      <span className="[overflow-wrap:anywhere]">
                        <span className="font-mono">{a.address}</span>
                        {range === null ? null : <span className="text-xs text-muted">{` · ${range}`}</span>}
                      </span>
                      {about.length > 0 ? <span className="text-xs text-muted">{about.join(" · ")}</span> : null}
                    </span>
                    <button
                      type="button"
                      aria-label={`Remove ${a.address}`}
                      disabled={remove.isPending}
                      className="ml-auto cursor-pointer rounded-pill px-2 text-muted hover:bg-brand-tint disabled:opacity-50"
                      onClick={() => {
                        remove.mutate(a);
                      }}
                    >
                      ×
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <form
            className="flex flex-wrap gap-2"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              if (typed.trim() !== "") {
                add.mutate(typed.trim());
              }
            }}
          >
            <label htmlFor={inputId} className="sr-only">
              {`Add a network address for ${client.name}`}
            </label>
            <input
              id={inputId}
              type="text"
              placeholder="192.0.2.10"
              autoComplete="off"
              spellCheck={false}
              value={typed}
              aria-invalid={refused === null ? undefined : true}
              aria-describedby={refused === null ? hintId : `${hintId} ${alertId}`}
              className={`${inputClass} min-w-0 flex-[1_1_140px] font-mono text-sm`}
              onChange={(event) => {
                setTyped(event.target.value);
                setRefused(null);
                setDone(null);
              }}
            />
            <button
              type="submit"
              disabled={add.isPending || typed.trim() === ""}
              className="h-10 cursor-pointer rounded-pill border border-line bg-card px-4 text-sm font-medium hover:bg-brand-tint disabled:cursor-default disabled:opacity-50"
            >
              Add
            </button>
          </form>
          <span id={hintId} className="text-xs text-muted">
            {`${possessive(client.name)} firewall WAN address, iDRACs and servers: an address, or a range of at most a /24 (IPv6: a /64). An address is one client's.`}
          </span>
          {refused !== null ? (
            <p id={alertId} role="alert" className="text-sm text-status-needs-client-fg">
              {refused}
            </p>
          ) : null}
          <p role="status" className="text-sm font-medium text-status-logged-fg empty:hidden">
            {done ?? ""}
          </p>
        </>
      )}
    </section>
  );
}

export function ClientDetails({
  id,
  client,
  clients,
  companies,
  sites,
  onSaved,
}: {
  id: string;
  client: ClientMapping;
  clients: ClientMapping[];
  companies: { data?: ScreenConnectCompanies | undefined; isError: boolean };
  sites: { data?: DattoSites | undefined; isError: boolean };
  onSaved: (record: ClientRecord) => void;
}) {
  return (
    <div
      id={id}
      role="region"
      aria-label={client.name}
      className="grid gap-6 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]"
    >
      <AutotaskSection client={client} onSaved={onSaved} />
      <ScreenConnectSection client={client} clients={clients} companies={companies} />
      <DattoSection client={client} clients={clients} sites={sites} />
      <PhonesSection client={client} />
      <EmailDomainsSection client={client} />
      <NetworkAddressesSection client={client} />
    </div>
  );
}
